"use client"

import * as React from "react"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
  MenubarCheckboxItem,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarLabel,
} from "../../components/menubar"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// UIMenubar Types
// ============================================================================

export interface UIMenubarItemBase {
  id?: string
  label: string
  disabled?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

export interface UIMenubarLinkItem extends UIMenubarItemBase {
  type: "link"
  href?: string
  onClick?: () => void
  shortcut?: string
}

export interface UIMenubarCheckboxItemData extends UIMenubarItemBase {
  type: "checkbox"
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  shortcut?: string
}

export interface UIMenubarRadioItemData extends UIMenubarItemBase {
  type: "radio"
  value: string
  shortcut?: string
}

export interface UIMenubarRadioGroupData {
  type: "radio-group"
  id?: string
  label?: string
  value?: string
  onValueChange?: (value: string) => void
  items: UIMenubarRadioItemData[]
}

export interface UIMenubarSeparatorData {
  type: "separator"
  id?: string
}

export interface UIMenubarLabelData {
  type: "label"
  id?: string
  label: string
}

export interface UIMenubarSubmenuData extends UIMenubarItemBase {
  type: "submenu"
  items: UIMenubarItemData[]
}

export type UIMenubarItemData =
  | UIMenubarLinkItem
  | UIMenubarCheckboxItemData
  | UIMenubarRadioGroupData
  | UIMenubarSeparatorData
  | UIMenubarLabelData
  | UIMenubarSubmenuData

export interface UIMenubarMenuData {
  id?: string
  label: string
  disabled?: boolean
  items: UIMenubarItemData[]
}

const menubarVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0", // Width Stability 패턴
  {
    variants: {
      variant: {
        default: "bg-background border border-border",
        minimal: "bg-transparent border-0",
        bordered: "bg-background border-b border-border",
      },
      size: {
        sm: "h-8 gap-0.5 p-0.5",
        default: "h-9 gap-1 p-1",
        lg: "h-10 gap-1.5 p-1.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface UIMenubarProps extends
  React.ComponentProps<typeof Menubar>,
  VariantProps<typeof menubarVariants> {
  /**
   * 메뉴 바 메뉴들
   */
  menus?: UIMenubarMenuData[]
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
}

// ============================================================================
// UIMenubar Component
// ============================================================================

export const UIMenubar = React.forwardRef<
  React.ComponentRef<typeof Menubar>,
  UIMenubarProps
>(({
  className,
  menus = [],
  variant,
  size,
  containerClassName,
  children,
  ...props
}, ref) => {

  const renderMenuItem = (item: UIMenubarItemData, index: number): React.ReactNode => {
    const key = item.id || `item-${index}`

    switch (item.type) {
      case "link":
        return (
          <MenubarItem
            key={key}
            disabled={item.disabled}
            onClick={item.onClick}
            className={cn(
              item.icon && "flex items-center gap-2"
            )}
          >
            {item.icon && <item.icon className="h-4 w-4" />}
            {item.label}
            {item.shortcut && <MenubarShortcut>{item.shortcut}</MenubarShortcut>}
          </MenubarItem>
        )

      case "checkbox":
        return (
          <MenubarCheckboxItem
            key={key}
            disabled={item.disabled}
            checked={item.checked}
            onCheckedChange={item.onCheckedChange}
          >
            {item.icon && <item.icon className="h-4 w-4" />}
            {item.label}
            {item.shortcut && <MenubarShortcut>{item.shortcut}</MenubarShortcut>}
          </MenubarCheckboxItem>
        )

      case "radio-group":
        return (
          <MenubarRadioGroup
            key={key}
            value={item.value}
            onValueChange={item.onValueChange}
          >
            {item.label && <MenubarLabel>{item.label}</MenubarLabel>}
            {item.items.map((radioItem, radioIndex) => (
              <MenubarRadioItem
                key={radioItem.id || `radio-${radioIndex}`}
                value={radioItem.value}
                disabled={radioItem.disabled}
              >
                {radioItem.icon && <radioItem.icon className="h-4 w-4" />}
                {radioItem.label}
                {radioItem.shortcut && <MenubarShortcut>{radioItem.shortcut}</MenubarShortcut>}
              </MenubarRadioItem>
            ))}
          </MenubarRadioGroup>
        )

      case "separator":
        return <MenubarSeparator key={key} />

      case "label":
        return <MenubarLabel key={key}>{item.label}</MenubarLabel>

      case "submenu":
        return (
          <MenubarSub key={key}>
            <MenubarSubTrigger disabled={item.disabled}>
              {item.icon && <item.icon className="h-4 w-4 mr-2" />}
              {item.label}
            </MenubarSubTrigger>
            <MenubarSubContent>
              {item.items.map((subItem, subIndex) => renderMenuItem(subItem, subIndex))}
            </MenubarSubContent>
          </MenubarSub>
        )

      default:
        return null
    }
  }

  const renderMenu = (menu: UIMenubarMenuData, index: number) => {
    return (
      <MenubarMenu key={menu.id || `menu-${index}`}>
        <MenubarTrigger disabled={menu.disabled}>
          {menu.label}
        </MenubarTrigger>
        <MenubarContent>
          {menu.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
        </MenubarContent>
      </MenubarMenu>
    )
  }

  if (menus.length === 0 && !children) {
    return null
  }

  return (
    <UIBox className={cn("w-full", containerClassName)}>
      <Menubar
        ref={ref}
        className={cn(menubarVariants({ variant, size }), className)}
        {...props}
      >
        {menus.map((menu, index) => renderMenu(menu, index))}
        {children}
      </Menubar>
    </UIBox>
  )
})

UIMenubar.displayName = "UIMenubar"

// ============================================================================
// Individual Component Exports
// ============================================================================

export const UIMenubarMenu = MenubarMenu
export const UIMenubarTrigger = MenubarTrigger
export const UIMenubarContent = MenubarContent
export const UIMenubarItem = MenubarItem
export const UIMenubarSeparator = MenubarSeparator
export const UIMenubarShortcut = MenubarShortcut
export const UIMenubarCheckboxItem = MenubarCheckboxItem
export const UIMenubarRadioGroup = MenubarRadioGroup
export const UIMenubarRadioItem = MenubarRadioItem
export const UIMenubarSub = MenubarSub
export const UIMenubarSubContent = MenubarSubContent
export const UIMenubarSubTrigger = MenubarSubTrigger
export const UIMenubarLabel = MenubarLabel

// ============================================================================
// Preset Menubar Components
// ============================================================================

export interface UIMenubarSimpleProps extends Omit<UIMenubarProps, 'menus'> {
  menus: UIMenubarMenuData[]
}

export const UIMenubarSimple = React.forwardRef<
  React.ComponentRef<typeof Menubar>,
  UIMenubarSimpleProps
>((props, ref) => {
  return <UIMenubar ref={ref} {...props} />
})

UIMenubarSimple.displayName = "UIMenubarSimple"

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations